import { isNumber } from "lodash";
import moment from "moment";
import BadgeListCellRenderer from "../../PPTPromosGrid/cell-renderers/BadgeListCellRenderer";
import SeverityCellRenderer from "../cell-renderers/SeverityCellRenderer";
import { VariesCellRenderer } from "../cell-renderers/VariesCellRenderer";
import { OpenDrawerCellRenderer } from "../cell-renderers/OpenDrawerCellRenderer";

const formatDateCell = ({ value }) => {
  if (value == null || value === "") return "";
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : String(value);
};

// — Helpers for VariesCellRenderer config on Cost / Retail / Rev Share / Currency —

const formatCurrencyValue = (v, data) =>
  v != null
    ? v.toLocaleString(undefined, {
        style: "currency",
        currency: data?.currency || "USD",
      })
    : "";

const formatCurrencyAt = (v, currency) =>
  v != null
    ? v.toLocaleString(undefined, {
        style: "currency",
        currency: currency || "USD",
      })
    : String(v);

/**
 * Build the tooltip range for a numeric currency-style column. Uses each
 * child's own currency where it differs; falls back to the parent currency
 * if all children agree.
 */
const buildCurrencyRange = (childField) => (childValues, data, children) => {
  const numeric = childValues.filter((v) => typeof v === "number");
  if (numeric.length === 0) return "";
  const min = Math.min(...numeric);
  const max = Math.max(...numeric);

  const currencies = new Set(
    children
      .filter((c) => c?.[childField] != null)
      .map((c) => c?.currency)
      .filter(Boolean),
  );
  const singleCurrency = currencies.size === 1 ? [...currencies][0] : null;

  if (min === max) {
    return `All clients: ${formatCurrencyAt(min, singleCurrency || "USD")}`;
  }
  if (singleCurrency) {
    return `Range: ${formatCurrencyAt(min, singleCurrency)} – ${formatCurrencyAt(
      max,
      singleCurrency,
    )}`;
  }
  // Mixed currencies: show per-client breakdown so the operator sees the unit
  return children
    .filter((c) => c?.[childField] != null)
    .map(
      (c) =>
        `${c.client || "?"}: ${formatCurrencyAt(c[childField], c.currency)}`,
    )
    .join(" · ");
};

const formatPercentValue = (v) =>
  isNumber(v)
    ? v.toLocaleString(undefined, {
        style: "percent",
        maximumFractionDigits: 2,
      })
    : "";

const formatPercentRange = (childValues) => {
  const nums = childValues.filter((v) => typeof v === "number");
  if (nums.length === 0) return "";
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const fmt = (v) =>
    v.toLocaleString(undefined, {
      style: "percent",
      maximumFractionDigits: 2,
    });
  return min === max
    ? `All clients: ${fmt(min)}`
    : `Range: ${fmt(min)} – ${fmt(max)}`;
};

const formatStringValue = (v) => (v != null ? String(v) : "");
const formatStringDistinct = (childValues) => {
  const distinct = [...new Set(childValues)];
  return distinct.length === 1
    ? `All clients: ${distinct[0]}`
    : distinct.join(", ");
};

export const defaultColumnDef = {
  resizable: true,
  wrapText: true,
  autoHeight: true,
  width: 80,
  sortable: true,
  filter: "agSetColumnFilter",
  suppressHeaderMenuButton: false,
  menuTabs: ["filterMenuTab"],
  // AG Grid 32 auto-infers cell data types from row values. Columns with
  // array values (`clients`, `language`) get inferred as "object" which
  // triggers a warning and can confuse rendering. We control rendering via
  // explicit cellRenderer props, so disable the inference.
  cellDataType: false,
  lockVisible: false,
  suppressColumnsToolPanel: false,
};

export const columnDefs = [
  {
    field: "issueSeverity",
    headerName: "Status",
    width: 100,
    cellRenderer: SeverityCellRenderer,
  },
  {
    field: "title",
    headerName: "Title",
    flex: 2,
    minWidth: 200,
  },
  {
    field: "territory",
    headerName: "Territory",
    width: 120,
    cellRenderer: BadgeListCellRenderer,
    cellRendererParams: { maxItems: 2 },
  },
  {
    field: "format",
    headerName: "Format",
    width: 120,
    cellRenderer: BadgeListCellRenderer,
  },
  {
    field: "media",
    headerName: "Type",
    width: 120,
    cellRenderer: BadgeListCellRenderer,
  },
  {
    field: "studio",
    headerName: "Studio",
    width: 120,
  },
  {
    field: "language",
    headerName: "Language",
    width: 120,
    cellRenderer: BadgeListCellRenderer,
    cellRendererParams: { maxItems: 2 },
  },
  {
    field: "clients",
    headerName: "Client Count",
    width: 120,
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      return Array.isArray(params.data?.clients)
        ? params.data.clients.length
        : 0;
    },
  },
  {
    // "Open details" action — pinned to the right edge so it's always reachable.
    // OpenDrawerCellRenderer owns its own click handler and stops propagation,
    // so a click here opens the drawer while clicks elsewhere on the row fall
    // through to the grid's master/detail toggle.
    colId: "actions",
    headerName: "",
    width: 48,
    maxWidth: 48,
    pinned: "right",
    sortable: false,
    filter: false,
    resizable: false,
    suppressHeaderMenuButton: true,
    cellRenderer: OpenDrawerCellRenderer,
  },
];
